import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Download, Mail, Phone, MapPin, Building2, Briefcase,
  Linkedin, Globe, Star, ChevronDown, ChevronUp, X,
  CheckCircle, Clock, Users, BookOpen, Github, AlertCircle,
  Loader2, ArrowUpDown, Award, Zap, TrendingUp, ShieldCheck, ShieldAlert, Smartphone
} from 'lucide-react';
import { useTheme } from './ThemeContext';

// ── API helpers ───────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || '';
const API_ROOT = API_BASE.endsWith('/api') ? API_BASE.slice(0, -4) : API_BASE;
const buildApiUrl = (path) => API_ROOT ? `${API_ROOT}${path}` : path;
const getToken = () => localStorage.getItem('token');

async function apiFetch(path, opts = {}) {
  const res = await fetch(buildApiUrl(path), {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}`, ...(opts.headers || {}) }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json();
}

// ── Pure helpers ──────────────────────────────────────────────────────────────
function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function timeAgo(date) {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function avatarColor(name) {
  const palette = ['#1E88E5','#10b981','#f59e0b','#8b5cf6','#ec4899','#0ea5e9','#ef4444','#6366f1'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}

const LOWER_WORDS = new Set(['a','an','the','and','but','or','for','nor','on','at','to','by','in','of','with','vs','via']);
function tc(str) {
  if (!str || typeof str !== 'string') return '';
  return str.toLowerCase().split(' ').map((w, i) =>
    (!w || (i > 0 && LOWER_WORDS.has(w))) ? w : w.charAt(0).toUpperCase() + w.slice(1)
  ).join(' ');
}

// ── Validation helpers ────────────────────────────────────────────────────────

function emailBadgeProps(v) {
  if (!v) return null;
  if (v.valid)   return { color: '#10b981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.28)', label: 'Verified', icon: ShieldCheck };
  if (v.risky)   return { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.28)', label: 'Risky',    icon: ShieldAlert };
  if (v.invalid) return { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.28)',  label: 'Invalid',  icon: ShieldAlert };
  return null;
}

function phoneTypeLabel(lineType) {
  if (!lineType) return null;
  if (lineType === 'mobile')   return { label: 'Mobile', color: '#10b981' };
  if (lineType === 'landline') return { label: 'Landline', color: '#64748b' };
  if (lineType === 'voip')     return { label: 'VoIP', color: '#f59e0b' };
  return null;
}

// ── Sub-components (top-level — never redefined on parent re-render) ──────────

function DrawerSection({ title, c, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: c.faint, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function DrawerRow({ icon: Icon, label, value, href, c }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
      <Icon size={14} style={{ color: c.faint, marginTop: 2, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 11, color: c.faint }}>{label}</div>
        {href
          ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: c.accent, textDecoration: 'none', fontWeight: 500 }}>{value}</a>
          : <div style={{ fontSize: 13, color: c.text }}>{value}</div>
        }
      </div>
    </div>
  );
}

function StatPill({ icon: Icon, label, value, color, c }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px', borderRadius: 12,
      background: c.card, border: `1px solid ${c.border}`,
      flex: '1 1 160px', minWidth: 140
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon size={17} style={{ color }} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color: c.text, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: c.sub, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

function ContactCard({ contact, c, onSelect }) {
  const color = avatarColor(contact.fullName);
  return (
    <div
      onClick={() => onSelect(contact)}
      style={{
        background: c.card, border: `1px solid ${c.border}`,
        borderRadius: 14, padding: '18px 18px 16px',
        cursor: 'pointer', transition: 'all 0.18s',
        position: 'relative'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = c.accent + '60';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 24px ${color}18`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = c.border;
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: '14px 14px 0 0' }} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 4 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: `linear-gradient(135deg, ${color}, ${color}cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: 0.5
        }}>
          {initials(contact.fullName)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: c.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {contact.fullName || 'Unknown'}
          </div>
          {contact.title && (
            <div style={{ fontSize: 12, color: c.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
              {tc(contact.title)}
            </div>
          )}
        </div>
        {contact.inAts && (
          <div title="Uploaded to ATS" style={{
            width: 22, height: 22, borderRadius: 6, flexShrink: 0,
            background: c.successBg, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <CheckCircle size={13} style={{ color: c.success }} />
          </div>
        )}
      </div>

      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {contact.company && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Building2 size={11} style={{ color: c.faint, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: c.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tc(contact.company)}</span>
          </div>
        )}
        {contact.location && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <MapPin size={11} style={{ color: c.faint, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: c.sub }}>{tc(contact.location)}</span>
          </div>
        )}
      </div>

      <div style={{ marginTop: 10, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {contact.email && (() => {
          const vBadge = contact.validation ? emailBadgeProps(contact.validation.email) : null;
          return vBadge ? (
            <span style={{ padding: '3px 8px', borderRadius: 6, background: vBadge.bg, color: vBadge.color, border: `1px solid ${vBadge.border}`, fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <vBadge.icon size={10} /> Email {vBadge.label}
            </span>
          ) : (
            <span style={{ padding: '3px 8px', borderRadius: 6, background: c.accentBg, color: c.accent, fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Mail size={10} /> Email
            </span>
          );
        })()}
        {contact.phone && (() => {
          const pType = contact.validation ? phoneTypeLabel(contact.validation.phone?.lineType) : null;
          return (
            <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(16,185,129,0.10)', color: pType?.color || c.success, fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              {pType?.label === 'Mobile' ? <Smartphone size={10} /> : <Phone size={10} />}
              {pType ? pType.label : 'Phone'}
            </span>
          );
        })()}
        {contact.linkedinUrl && (
          <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(10,102,194,0.10)', color: '#0a66c2', fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Linkedin size={10} /> LinkedIn
          </span>
        )}
        {contact.validation?.summary?.fullyVerified && (
          <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <ShieldCheck size={10} /> Verified
          </span>
        )}
      </div>

      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, overflow: 'hidden' }}>
          {(contact.skills || []).slice(0, 2).map((s, i) => (
            <span key={i} style={{ padding: '2px 7px', borderRadius: 4, background: c.muted, color: c.faint, fontSize: 10, whiteSpace: 'nowrap' }}>{tc(s)}</span>
          ))}
          {(contact.skills || []).length > 2 && (
            <span style={{ padding: '2px 7px', borderRadius: 4, background: c.muted, color: c.faint, fontSize: 10 }}>+{contact.skills.length - 2}</span>
          )}
        </div>
        <span style={{ fontSize: 10, color: c.faint, whiteSpace: 'nowrap', marginLeft: 4 }}>{timeAgo(contact.enrichedAt)}</span>
      </div>
    </div>
  );
}

function DetailDrawer({ contact, onClose, c, darkMode }) {
  if (!contact) return null;
  const color = avatarColor(contact.fullName);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 }}
      />
      {/* Drawer panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, maxWidth: '95vw',
        background: c.drawerBg, borderLeft: `1px solid ${c.border}`,
        zIndex: 1001, display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.22)', overflowY: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 22px 16px',
          background: `linear-gradient(135deg, ${color}22, ${color}06)`,
          borderBottom: `1px solid ${c.border}`, flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0, flex: 1 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 19, fontWeight: 700,
                boxShadow: `0 4px 14px ${color}44`
              }}>
                {initials(contact.fullName)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: c.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tc(contact.fullName) || contact.fullName}</div>
                {contact.title && <div style={{ fontSize: 12, color: c.sub, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tc(contact.title)}</div>}
                {contact.company && <div style={{ fontSize: 12, color: c.faint, marginTop: 1 }}>{tc(contact.company)}</div>}
              </div>
            </div>
            <button onClick={onClose} style={{
              width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', flexShrink: 0, marginLeft: 10,
              background: darkMode ? '#334155' : '#f3f4f6', color: c.sub,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <X size={16} />
            </button>
          </div>

          {/* Quick-contact chips */}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {contact.email && (() => {
              const vBadge = contact.validation ? emailBadgeProps(contact.validation.email) : null;
              return (
                <a href={`mailto:${contact.email}`} style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: vBadge ? vBadge.bg : c.accentBg,
                  color: vBadge ? vBadge.color : c.accent,
                  border: `1px solid ${vBadge ? vBadge.border : c.accentBorder}`,
                  textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5
                }}>
                  {vBadge ? <vBadge.icon size={12} /> : <Mail size={12} />} {contact.email}
                  {vBadge && <span style={{ fontSize: 10, opacity: 0.8 }}>· {vBadge.label}</span>}
                </a>
              );
            })()}
            {contact.phone && (() => {
              const pType = contact.validation ? phoneTypeLabel(contact.validation.phone?.lineType) : null;
              return (
                <a href={`tel:${contact.phone}`} style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: 'rgba(16,185,129,0.10)', color: pType?.color || c.success, border: `1px solid ${c.success}30`,
                  textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5
                }}>
                  {pType?.label === 'Mobile' ? <Smartphone size={12} /> : <Phone size={12} />}
                  {contact.phone}
                  {pType && <span style={{ fontSize: 10, opacity: 0.8 }}>· {pType.label}</span>}
                </a>
              );
            })()}
            {contact.linkedinUrl && (
              <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: 'rgba(10,102,194,0.10)', color: '#0a66c2', border: '1px solid rgba(10,102,194,0.25)',
                textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5
              }}>
                <Linkedin size={12} /> LinkedIn
              </a>
            )}
            {contact.validation?.summary?.fullyVerified && (
              <span style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.35)',
                display: 'flex', alignItems: 'center', gap: 5
              }}>
                <ShieldCheck size={12} /> Contact Verified
              </span>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>

          <DrawerSection title="Contact Info" c={c}>
            <DrawerRow c={c} icon={Mail}     label="Email"       value={contact.email}       href={`mailto:${contact.email}`} />
            <DrawerRow c={c} icon={Mail}     label="Work Email"  value={contact.workEmail && contact.workEmail !== contact.email ? contact.workEmail : null} href={`mailto:${contact.workEmail}`} />
            <DrawerRow c={c} icon={Phone}    label="Phone"       value={contact.phone}       href={`tel:${contact.phone}`} />
            <DrawerRow c={c} icon={Phone}    label="Work Phone"  value={contact.workPhone && contact.workPhone !== contact.phone ? contact.workPhone : null} />
            <DrawerRow c={c} icon={MapPin}   label="Location"    value={contact.location} />
            <DrawerRow c={c} icon={Globe}    label="Website"     value={contact.companyWebsite} href={contact.companyWebsite ? (contact.companyWebsite.startsWith('http') ? contact.companyWebsite : `https://${contact.companyWebsite}`) : null} />
            {contact.githubUrl && <DrawerRow c={c} icon={Github} label="GitHub" value={contact.githubUrl} href={contact.githubUrl} />}
          </DrawerSection>

          <DrawerSection title="Professional" c={c}>
            <DrawerRow c={c} icon={Briefcase} label="Title"      value={tc(contact.title)} />
            <DrawerRow c={c} icon={Building2} label="Company"    value={tc(contact.company)} />
            <DrawerRow c={c} icon={Star}      label="Industry"   value={tc(contact.industry)} />
            <DrawerRow c={c} icon={MapPin}    label="Location"   value={tc(contact.location)} />
            <DrawerRow c={c} icon={Clock}     label="Experience" value={contact.yearsExperience ? `${contact.yearsExperience} years` : null} />
            {contact.inferredSalary && <DrawerRow c={c} icon={Zap} label="Est. Salary" value={contact.inferredSalary} />}
          </DrawerSection>

          {contact.summary && (
            <DrawerSection title="Summary" c={c}>
              <p style={{ fontSize: 13, color: c.sub, lineHeight: 1.6, margin: 0 }}>{contact.summary}</p>
            </DrawerSection>
          )}

          {contact.skills?.length > 0 && (
            <DrawerSection title="Skills" c={c}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {contact.skills.map((s, i) => (
                  <span key={i} style={{ padding: '4px 10px', borderRadius: 6, background: c.muted, color: c.sub, fontSize: 12, border: `1px solid ${c.border}` }}>{tc(s)}</span>
                ))}
              </div>
            </DrawerSection>
          )}

          {contact.experience?.length > 0 && (
            <DrawerSection title="Experience" c={c}>
              {contact.experience.slice(0, 5).map((exp, i) => (
                <div key={i} style={{ marginBottom: 14, paddingLeft: 12, borderLeft: `2px solid ${c.accent}50` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{tc(exp.title || exp.company)}</div>
                  {exp.company && exp.title && <div style={{ fontSize: 12, color: c.sub, marginTop: 1 }}>{tc(exp.company)}</div>}
                  {exp.location && <div style={{ fontSize: 11, color: c.faint, marginTop: 1 }}>{tc(exp.location)}</div>}
                  {(exp.start_date || exp.end_date) && (
                    <div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>
                      {exp.start_date || ''}{exp.start_date && exp.end_date ? ' — ' : ''}{exp.end_date || 'Present'}
                    </div>
                  )}
                </div>
              ))}
            </DrawerSection>
          )}

          {contact.education?.length > 0 && (
            <DrawerSection title="Education" c={c}>
              {contact.education.slice(0, 3).map((edu, i) => (
                <div key={i} style={{ marginBottom: 12, paddingLeft: 12, borderLeft: `2px solid ${c.success}50` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{tc(edu.school?.name || edu.school || '')}</div>
                  {edu.degrees?.length > 0 && (
                    <div style={{ fontSize: 12, color: c.sub, marginTop: 1 }}>
                      {tc(edu.degrees[0])}{edu.majors?.[0] ? ` — ${tc(edu.majors[0])}` : ''}
                    </div>
                  )}
                  {(edu.start_date || edu.end_date) && (
                    <div style={{ fontSize: 11, color: c.faint, marginTop: 1 }}>
                      {edu.start_date || ''}{edu.start_date && edu.end_date ? ' — ' : ''}{edu.end_date || ''}
                    </div>
                  )}
                </div>
              ))}
            </DrawerSection>
          )}

          {contact.certifications?.length > 0 && (
            <DrawerSection title="Certifications" c={c}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {contact.certifications.slice(0, 8).map((cert, i) => (
                  <span key={i} style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.08)', color: c.warn, fontSize: 12, border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Award size={10} />{tc(cert.name || cert)}
                  </span>
                ))}
              </div>
            </DrawerSection>
          )}

          {contact.languages?.length > 0 && (
            <DrawerSection title="Languages" c={c}>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {contact.languages.map((l, i) => (
                  <span key={i} style={{ padding: '4px 10px', borderRadius: 6, background: c.muted, color: c.sub, fontSize: 12 }}>{typeof l === 'string' ? l : l.name || ''}</span>
                ))}
              </div>
            </DrawerSection>
          )}

          {contact.validation && (
            <DrawerSection title="Contact Validation" c={c}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {contact.validation.email && (() => {
                  const vb = emailBadgeProps(contact.validation.email);
                  if (!vb) return null;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: vb.bg, border: `1px solid ${vb.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <vb.icon size={13} style={{ color: vb.color }} />
                        <span style={{ fontSize: 12, color: vb.color, fontWeight: 600 }}>Email {vb.label}</span>
                      </div>
                      <div style={{ fontSize: 11, color: c.faint, textAlign: 'right' }}>
                        {contact.validation.email.subStatus && <div>{contact.validation.email.subStatus}</div>}
                        {contact.validation.email.freeEmail && <div>Free email domain</div>}
                        {contact.validation.email.didYouMean && <div style={{ color: c.warn }}>Did you mean: {contact.validation.email.didYouMean}?</div>}
                      </div>
                    </div>
                  );
                })()}
                {contact.validation.phone && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: contact.validation.phone.valid ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${contact.validation.phone.valid ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      {contact.validation.phone.isMobile ? <Smartphone size={13} style={{ color: '#10b981' }} /> : <Phone size={13} style={{ color: c.faint }} />}
                      <span style={{ fontSize: 12, fontWeight: 600, color: contact.validation.phone.valid ? c.success : c.danger }}>
                        Phone {contact.validation.phone.valid ? 'Valid' : 'Invalid'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: c.faint, textAlign: 'right' }}>
                      {contact.validation.phone.lineType && <div style={{ textTransform: 'capitalize' }}>{contact.validation.phone.lineType}</div>}
                      {contact.validation.phone.carrier && <div>{contact.validation.phone.carrier}</div>}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>
                  Validated {contact.validation.validatedAt ? timeAgo(contact.validation.validatedAt) : ''}
                </div>
              </div>
            </DrawerSection>
          )}

          <DrawerSection title="Record Info" c={c}>
            <div style={{ fontSize: 12, color: c.faint }}>
              <div>Enriched: {contact.enrichedAt ? new Date(contact.enrichedAt).toLocaleString() : '—'}</div>
              {contact.inAts && (
                <div style={{ marginTop: 4, color: c.success, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CheckCircle size={12} /> Added to ATS
                </div>
              )}
            </div>
          </DrawerSection>
        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function MyEnrichedContacts() {
  const { darkMode } = useTheme();

  const c = {
    bg:           darkMode ? '#0f172a' : '#f4f6f8',
    card:         darkMode ? '#1e293b' : '#ffffff',
    muted:        darkMode ? '#0f172a' : '#f8fafc',
    border:       darkMode ? '#334155' : '#e5e7eb',
    text:         darkMode ? '#f8fafc' : '#111827',
    sub:          darkMode ? '#94a3b8' : '#64748b',
    faint:        darkMode ? '#475569' : '#9ca3af',
    accent:       '#1E88E5',
    accentBg:     'rgba(30,136,229,0.10)',
    accentBorder: 'rgba(30,136,229,0.25)',
    success:      '#10b981',
    successBg:    'rgba(16,185,129,0.10)',
    warn:         '#f59e0b',
    danger:       '#ef4444',
    drawerBg:     darkMode ? '#1e293b' : '#ffffff',
  };

  const LIMIT = 50;

  const [contacts,    setContacts]    = useState([]);
  const [stats,       setStats]       = useState({ total: 0, thisWeek: 0, thisMonth: 0, thisYear: 0, emailVerified: 0, emailRisky: 0, emailInvalid: 0, phoneValid: 0, validationCoverage: 0 });
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [search,      setSearch]      = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy,      setSortBy]      = useState('created_at');
  const [sortDir,     setSortDir]     = useState('desc');
  const [offset,      setOffset]      = useState(0);
  const [total,       setTotal]       = useState(0);
  const [selected,    setSelected]    = useState(null);
  const [exporting,   setExporting]   = useState(false);

  const load = useCallback(async (opts = {}) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({
        limit:   LIMIT,
        offset:  opts.offset  ?? offset,
        sortBy:  opts.sortBy  ?? sortBy,
        sortDir: opts.sortDir ?? sortDir,
        ...(search ? { search } : {})
      });
      const data = await apiFetch(`/api/knine/my-contacts?${params}`);
      setContacts(data.records || []);
      setTotal(data.total || 0);
      setStats({
        total:              data.total              || 0,
        thisWeek:           data.thisWeek           || 0,
        thisMonth:          data.thisMonth          || 0,
        thisYear:           data.thisYear           || 0,
        emailVerified:      data.emailVerified      || 0,
        emailRisky:         data.emailRisky         || 0,
        emailInvalid:       data.emailInvalid       || 0,
        phoneValid:         data.phoneValid         || 0,
        validationCoverage: data.validationCoverage || 0,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [offset, sortBy, sortDir, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setOffset(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSort = (field) => {
    const nextDir = sortBy === field && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(field); setSortDir(nextDir); setOffset(0);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(buildApiUrl('/api/knine/my-contacts/csv'), {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `my-contacts-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 4px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: c.text, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: c.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BookOpen size={19} style={{ color: c.accent }} />
            </div>
            My Contacts
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: c.sub }}>
            Every candidate you've enriched through kNine — full contact details, saved privately to your library.
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={exporting || stats.total === 0}
          style={{
            padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
            background: darkMode ? '#1e293b' : '#fff',
            color: c.accent, border: `1px solid ${c.accentBorder}`,
            opacity: exporting || stats.total === 0 ? 0.5 : 1
          }}
        >
          {exporting ? <Loader2 size={14} /> : <Download size={14} />}
          Export CSV
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: stats.validationCoverage > 0 ? 16 : 24, flexWrap: 'wrap' }}>
        <StatPill c={c} icon={Users}      label="Total Contacts"     value={stats.total.toLocaleString()}     color={c.accent} />
        <StatPill c={c} icon={Zap}        label="Enriched This Week" value={stats.thisWeek.toLocaleString()}  color="#f59e0b" />
        <StatPill c={c} icon={TrendingUp} label="This Month"         value={stats.thisMonth.toLocaleString()} color={c.success} />
        <StatPill c={c} icon={Award}      label="This Year"          value={stats.thisYear.toLocaleString()}  color="#8b5cf6" />
      </div>

      {/* Contact Health Report — only shown when validation data exists */}
      {stats.validationCoverage > 0 && (
        <div style={{ marginBottom: 24, padding: '16px 20px', borderRadius: 14, background: c.card, border: `1px solid ${c.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <ShieldCheck size={15} style={{ color: '#10b981' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Contact Health Report</span>
            <span style={{ fontSize: 11, color: c.faint, marginLeft: 4 }}>{stats.validationCoverage}% of contacts validated</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px', padding: '10px 14px', borderRadius: 10, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981' }}>{stats.emailVerified.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: c.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                <ShieldCheck size={10} /> Email Verified
              </div>
            </div>
            <div style={{ flex: '1 1 140px', padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>{stats.emailRisky.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: c.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                <ShieldAlert size={10} /> Risky / Catch-all
              </div>
            </div>
            <div style={{ flex: '1 1 140px', padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#ef4444' }}>{stats.emailInvalid.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: c.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                <AlertCircle size={10} /> Invalid Email
              </div>
            </div>
            <div style={{ flex: '1 1 140px', padding: '10px 14px', borderRadius: 10, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981' }}>{stats.phoneValid.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: c.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Smartphone size={10} /> Mobile Numbers
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search + sort bar */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap',
        padding: '12px 16px', background: c.card, borderRadius: 12, border: `1px solid ${c.border}`
      }}>
        <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: c.faint }} />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by name, title, company, email…"
            style={{
              width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8,
              border: `1px solid ${c.border}`, background: c.muted,
              color: c.text, fontSize: 13, outline: 'none', boxSizing: 'border-box'
            }}
          />
          {searchInput && (
            <button onClick={() => setSearchInput('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: c.faint }}>
              <X size={13} />
            </button>
          )}
        </div>

        {[{ key: 'created_at', label: 'Newest' }, { key: 'full_name', label: 'Name A–Z' }].map(s => (
          <button
            key={s.key}
            onClick={() => handleSort(s.key)}
            style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
              background: sortBy === s.key ? c.accentBg : 'transparent',
              color: sortBy === s.key ? c.accent : c.sub,
              border: `1px solid ${sortBy === s.key ? c.accentBorder : c.border}`
            }}
          >
            <ArrowUpDown size={12} />
            {s.label}
            {sortBy === s.key && (sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)}
          </button>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', fontSize: 12, color: c.faint }}>
          {loading && <Loader2 size={13} style={{ marginRight: 6 }} />}
          {!loading && `${total.toLocaleString()} contact${total !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px', borderRadius: 10, marginBottom: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <AlertCircle size={16} style={{ color: c.danger, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: c.danger }}>{error}</span>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: 18, height: 180 }}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div className="kubi-skeleton" style={{ width: 44, height: 44, borderRadius: 12 }} />
                <div style={{ flex: 1 }}>
                  <div className="kubi-skeleton" style={{ height: 14, borderRadius: 4, marginBottom: 6 }} />
                  <div className="kubi-skeleton" style={{ height: 12, borderRadius: 4, width: '70%' }} />
                </div>
              </div>
              <div className="kubi-skeleton" style={{ height: 12, borderRadius: 4, marginBottom: 6 }} />
              <div className="kubi-skeleton" style={{ height: 12, borderRadius: 4, width: '80%' }} />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && contacts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: c.card, borderRadius: 16, border: `1px solid ${c.border}` }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, margin: '0 auto 16px', background: c.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOpen size={28} style={{ color: c.accent }} />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: c.text }}>
            {search ? 'No contacts match your search' : 'No contacts yet'}
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: c.sub, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
            {search
              ? 'Try adjusting your search terms.'
              : 'Run a kNine search, find a great candidate, and click Enrich to unlock their full contact info. Every contact you enrich will be saved here — private to you.'}
          </p>
          {search && (
            <button onClick={() => setSearchInput('')} style={{ marginTop: 14, padding: '8px 18px', borderRadius: 8, border: `1px solid ${c.accentBorder}`, background: c.accentBg, color: c.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Clear Search
            </button>
          )}
        </div>
      )}

      {/* Contact grid */}
      {!loading && contacts.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {contacts.map(contact => (
              <ContactCard key={contact.id} contact={contact} c={c} onSelect={setSelected} />
            ))}
          </div>

          {total > LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
              <button
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                disabled={offset === 0}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${c.border}`, background: c.card, color: offset === 0 ? c.faint : c.text, opacity: offset === 0 ? 0.4 : 1 }}
              >
                ← Previous
              </button>
              <span style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: c.sub, padding: '0 8px' }}>
                {offset + 1}–{Math.min(offset + LIMIT, total)} of {total.toLocaleString()}
              </span>
              <button
                onClick={() => setOffset(offset + LIMIT)}
                disabled={offset + LIMIT >= total}
                style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${c.border}`, background: c.card, color: offset + LIMIT >= total ? c.faint : c.text, opacity: offset + LIMIT >= total ? 0.4 : 1 }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail drawer — rendered at page root so z-index is unaffected by card stacking */}
      {selected && (
        <DetailDrawer
          contact={selected}
          onClose={() => setSelected(null)}
          c={c}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}
